import React from 'react';
import { Button } from 'equasion-lib';
import { FiCheckCircle, FiRotateCcw, FiX } from 'react-icons/fi';
import PickShortReasonForm from '../PickShortReasonForm/PickShortReasonForm';
import {
  pickConfirmedStopAt,
  pickDate,
  pickNextStopNote,
  pickScannedCount,
  type PickLine,
  type PickWalkStop
} from '../../pickLineSteps';

type PickUnitScanPanelProps = {
  line: PickLine;
  /** The zone being scanned at (the one the first unit was confirmed at). */
  zoneId?: string | null;
  /** The whole walk, to say where the line's other zones are. */
  stops?: readonly PickWalkStop[] | null;
  /** A scan, removal or short is in flight. */
  busy: boolean;
  shortEntryOpen: boolean;
  onDone: () => void;
  onUndoLast: () => void;
  onRemoveSerial: (serialNumber: string) => void;
  /** "Couldn't find them all here": this zone gave the units scanned at it; the rest moves on. */
  onZoneShort: () => void;
  onOpenShortEntry: () => void;
  onCloseShortEntry: () => void;
  onRecordShort: (reason: string) => void;
};

const click = (onClick: () => void) => ({ onClick });

/**
 * Step 3 of a unit-scan (controlled) line: every unit is scanned — the camera stays live above this
 * panel — and the count only ever moves with a scan (or a removal). The panel shows the progress
 * ("3 of 5 scanned", and this zone's share), every serial scanned with its lot and expiration, and
 * the ways out of a mistake: Undo last scan, remove a given serial, "Couldn't find them all here"
 * (the zone gave what was scanned; the rest moves to the line's other zones), and Record short pick,
 * which takes a reason instead of a quantity. Done closes the sheet — the line is picked only once
 * every unit is scanned; until then it stays on the list to come back to.
 */
function PickUnitScanPanel({
  line,
  zoneId = null,
  stops = null,
  busy,
  shortEntryOpen,
  onDone,
  onUndoLast,
  onRemoveSerial,
  onZoneShort,
  onOpenShortEntry,
  onCloseShortEntry,
  onRecordShort
}: PickUnitScanPanelProps) {
  if (shortEntryOpen) {
    return (
      <PickShortReasonForm
        line={line}
        busy={busy}
        onSubmit={onRecordShort}
        onCancel={onCloseShortEntry}
      />
    );
  }

  const scanned = pickScannedCount(line);
  const complete = line.status === 'picked' || scanned >= line.quantity;
  const here = pickConfirmedStopAt(line, zoneId);
  const scannedHere = here?.scannedQuantity ?? 0;
  const zoneOpen = !!here && scannedHere < here.quantity;
  const next = !complete && !zoneOpen ? pickNextStopNote(line, stops) : null;
  // Newest first: the unit just scanned is the one a mistake is usually about.
  const units = [...(line.scannedUnits || [])].reverse();
  const percent = line.quantity > 0 ? Math.min(100, Math.round((scanned / line.quantity) * 100)) : 0;

  return (
    <div className="pick-unit-scan">
      <div className="pick-unit-scan__progress">
        <span className="pick-unit-scan__count" aria-live="polite">
          <strong>{scanned}</strong> of {line.quantity} scanned
        </span>
        <div
          className="pick-unit-scan__bar"
          role="progressbar"
          aria-label="Units scanned"
          aria-valuemin={0}
          aria-valuemax={line.quantity}
          aria-valuenow={scanned}
        >
          <span style={{ width: `${percent}%` }} />
        </div>
        {here?.zone && !complete && (
          <small className="pick-unit-scan__here">
            {here.zone.name}: {scannedHere} of {here.quantity} here
          </small>
        )}
      </div>

      {complete ? (
        <p className="pick-unit-scan__note pick-unit-scan__note--done">
          <FiCheckCircle aria-hidden /> All {line.quantity} scanned — the line is picked.
        </p>
      ) : next ? (
        <p
          className={[
            'pick-unit-scan__note',
            next.kind === 'noStock' && 'pick-unit-scan__note--warn'
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {next.text}
        </p>
      ) : (
        <p className="pick-unit-scan__note">
          Scan each unit&apos;s 2D barcode. The quantity is counted from the scans.
        </p>
      )}

      {units.length > 0 && (
        <ul className="pick-unit-scan__units" aria-label="Units scanned">
          {units.map((unit) => {
            const expiration = pickDate(unit.expiration);
            return (
              <li key={`${unit.serialNumber}-${unit.scannedAt ?? ''}`}>
                <span className="pick-unit-scan__serial">
                  SN {unit.serialNumber}
                  <small>
                    {[
                      unit.lot && `Lot ${unit.lot}`,
                      expiration && `Exp ${expiration}`,
                      unit.zone?.name
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                </span>
                <button
                  type="button"
                  className="pick-unit-scan__remove"
                  aria-label={`Remove serial ${unit.serialNumber}`}
                  disabled={busy}
                  onClick={() => onRemoveSerial(unit.serialNumber)}
                >
                  <FiX aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="pick-unit-scan__actions">
        <Button type={complete ? 'primary' : 'secondary'} fullWidth disabled={busy} {...click(onDone)}>
          Done
        </Button>
        {scanned > 0 && (
          <Button type="secondary" fullWidth disabled={busy} {...click(onUndoLast)}>
            <FiRotateCcw aria-hidden /> Undo last scan
          </Button>
        )}
        {!complete && zoneOpen && scannedHere > 0 && (
          <Button type="secondary" fullWidth disabled={busy} {...click(onZoneShort)}>
            Couldn&apos;t find them all here
          </Button>
        )}
        {!complete && (
          <Button type="secondary" fullWidth disabled={busy} {...click(onOpenShortEntry)}>
            Record short pick
          </Button>
        )}
      </div>
    </div>
  );
}

export default PickUnitScanPanel;
